// "Connect": exact snippets for one namespace, with a Cargo tab and a Containers tab (ADR-0033 section 12).
import { useState } from "react";
import type { TenantDetail } from "../api/types";
import { useServices } from "../components/context";
import { CodeBlock, ErrorBanner, Section } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { initialNamespace, lastNamespace, rememberNamespace } from "../lib/namespaces";
import { routeHash, type RouteType } from "../lib/router";
import {
  cargoConfigToml,
  cargoDependency,
  dockerCommands,
  gitlabCiDockerYaml,
  gitlabCiYaml,
  imageName,
  indexUrl,
  kubernetesPullSecret,
  registryName,
  type OciSnippetInput,
  type SnippetInput,
} from "../lib/snippets";

const TABS: { label: string; type: RouteType }[] = [
  { label: "Cargo", type: "cargo" },
  { label: "Containers", type: "oci" },
];

/** `ns` is the namespace in context (a link from the Namespaces page). `type` is the tab; Cargo is the default. */
export function Connect({
  tenant,
  ns: fromLink,
  type,
}: {
  tenant: TenantDetail;
  ns?: string;
  type?: RouteType;
}) {
  const { api } = useServices();
  const nss = useLoad(() => api.listNamespaces(tenant.slug), [tenant.slug]);
  const [chosen, setChosen] = useState("");
  const [last] = useState(() => lastNamespace(tenant.slug));
  const slugs = (nss.data?.items ?? []).map((n) => n.slug);
  const ns = chosen || initialNamespace(slugs.length ? slugs : ["default"], fromLink, last);
  const tab: RouteType = type ?? "cargo";

  return (
    <>
      <Section
        title="Connect"
        actions={
          <nav className="filter type-switch" aria-label="Package type">
            {TABS.map((t) => (
              <a
                key={t.type}
                className="button-link"
                href={routeHash({
                  name: "tenant",
                  tenant: tenant.slug,
                  page: "connect",
                  ns,
                  type: t.type === "cargo" ? undefined : t.type,
                })}
                aria-current={t.type === tab ? "page" : undefined}
              >
                {t.label}
              </a>
            ))}
          </nav>
        }
      >
        <ErrorBanner error={nss.error} />
        <div className="ns-pick">
          <label className="field-primary">
            Namespace
            <select
              value={ns}
              onChange={(e) => {
                setChosen(e.target.value);
                rememberNamespace(tenant.slug, e.target.value);
              }}
            >
              {(slugs.length ? slugs : ["default"]).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          {tab === "cargo" ? <CargoFacts tenant={tenant} ns={ns} /> : <OciFacts tenant={tenant} ns={ns} />}
        </div>
      </Section>
      {tab === "cargo" ? <CargoTab tenant={tenant} ns={ns} /> : <ContainersTab tenant={tenant} ns={ns} />}
    </>
  );
}

function CargoFacts({ tenant, ns }: { tenant: TenantDetail; ns: string }) {
  const { config } = useServices();
  return (
    <>
      <dl className="ns-facts">
        <dt>Cargo registry name</dt>
        <dd>
          <code data-testid="registry-name">{registryName(tenant.slug, ns)}</code>
        </dd>
        <dt>Index URL</dt>
        <dd>
          <code>{indexUrl(config.cargoBaseUrl, tenant.slug, ns)}</code>
        </dd>
      </dl>
      <p className="muted small">
        Each namespace is one Cargo registry. The registry name is the tenant slug for "default", and{" "}
        <code>{"<tenant>-<namespace>"}</code> for other namespaces. The snippets below use the namespace you
        choose here.
      </p>
    </>
  );
}

function ociInput(tenant: TenantDetail, ns: string, ociHost: string, audience: string): OciSnippetInput {
  return { tenant: tenant.slug, namespace: ns, ociHost, audience };
}

function OciFacts({ tenant, ns }: { tenant: TenantDetail; ns: string }) {
  const { config } = useServices();
  const input = ociInput(tenant, ns, config.ociHost, config.subjectAudience);
  return (
    <>
      <dl className="ns-facts">
        <dt>Registry host</dt>
        <dd>
          <code data-testid="oci-host">{config.ociHost}</code>
        </dd>
        <dt>Image name</dt>
        <dd>
          <code data-testid="image-name">{imageName(input, "<repo>")}:&lt;tag&gt;</code>
        </dd>
      </dl>
      <p className="muted small">
        An image name always has the tenant slug and the namespace, "default" too. The repository part can
        have 1 to 4 lowercase components, for example <code>apps/web</code>. The first allowed push creates
        the repository.
      </p>
    </>
  );
}

function grantLink(tenant: TenantDetail, ns: string) {
  return (
    <a href={routeHash({ name: "tenant", tenant: tenant.slug, page: "grants", ns })}>
      grant on namespace <code>{ns}</code>
    </a>
  );
}

function CargoTab({ tenant, ns }: { tenant: TenantDetail; ns: string }) {
  const { config } = useServices();
  const input: SnippetInput = {
    tenant: tenant.slug,
    namespace: ns,
    cargoBaseUrl: config.cargoBaseUrl,
    stsUrl: config.stsUrl,
    audience: config.subjectAudience,
  };
  const reg = registryName(tenant.slug, ns);
  return (
    <>
      <Section title="Connect Cargo">
        <ol className="steps">
          <li>
            Install the credential provider (Cargo 1.74 or newer):{" "}
            <code>cargo install cargo-credential-prego</code>
          </li>
          <li>Add a principal and a {grantLink(tenant, ns)} for the machine.</li>
          <li>Add this file to the project or to the user Cargo home.</li>
        </ol>
        <CodeBlock title=".cargo/config.toml" code={cargoConfigToml(input)} />
        <CodeBlock title="Cargo.toml" code={cargoDependency(input)} />
      </Section>
      <Section title="GitLab CI">
        <p className="muted">
          The job gets a GitLab ID token for Prego. The credential provider exchanges it at the STS. Add an
          OIDC trust config for your GitLab and a principal that matches the project.
        </p>
        <CodeBlock title=".gitlab-ci.yml" code={gitlabCiYaml(input)} />
      </Section>
      <Section title="Other identity sources">
        <ul>
          <li>
            API key: set <code>PREGO_API_KEY</code> to the key of an API key principal.
          </li>
          <li>
            GitHub Actions: add <code>permissions: id-token: write</code>. The provider requests the token.
          </li>
          <li>
            SPIFFE: set <code>SPIFFE_ENDPOINT_SOCKET</code>, or <code>PREGO_SVID_FILE</code> for a JWT-SVID
            file.
          </li>
          <li>
            Other OIDC tokens (Kubernetes, AWS, GCP): set <code>PREGO_TOKEN_FILE</code>.
          </li>
        </ul>
        <p className="muted">
          Publish with <code>cargo publish --registry {reg}</code>.
        </p>
      </Section>
    </>
  );
}

function ContainersTab({ tenant, ns }: { tenant: TenantDetail; ns: string }) {
  const { config } = useServices();
  const input = ociInput(tenant, ns, config.ociHost, config.subjectAudience);
  return (
    <>
      <Section title="Docker and Podman">
        <ol className="steps">
          <li>
            Add a principal and a {grantLink(tenant, ns)} with the package type "Containers" or "Any type".
            "read" pulls, "publish" pushes, and "delete" deletes tags and manifests.
          </li>
          <li>
            Log in with the tenant slug as the username. The password is an API key of the principal, or an ID
            token in CI. Podman takes the same commands.
          </li>
        </ol>
        <CodeBlock title="Shell" code={dockerCommands(input)} />
        <p className="muted small">A stock docker push sends a layer of at most 95 MiB.</p>
      </Section>
      <Section title="GitLab CI">
        <p className="muted">
          The job gets a GitLab ID token for Prego and uses it as the password. The token expires after the
          job. Add an OIDC trust config for your GitLab and a principal that matches the project.
        </p>
        <CodeBlock title=".gitlab-ci.yml" code={gitlabCiDockerYaml(input)} />
      </Section>
      <Section title="Kubernetes">
        <p className="muted">
          Put a read-only API key in an image pull secret. Then add{" "}
          <code>{"imagePullSecrets: [{ name: prego-pull }]"}</code> to the pod spec.
        </p>
        <p className="banner warn">
          An API key expires in at most 90 days. Make a new key and update the secret before it expires.
        </p>
        <CodeBlock title="Shell" code={kubernetesPullSecret(input)} />
      </Section>
    </>
  );
}
